import api from '@/libs/axiosInstance';
import type { ApiResponse, DemoAvailabilityDto, DemoInvitationDto } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

export const DEMO_INVITATION_PATH = '/dashboard/demo-invitation';

export const demoInvitationKeys = {
  all: ['demo-invitation'] as const,
  brand: (brandId: string) => [...demoInvitationKeys.all, brandId] as const,
  availabilityAll: ['demo-invitation', 'availability'] as const,
  availability: (timeZone: string) => [...demoInvitationKeys.availabilityAll, timeZone] as const,
};

/** The zone the slots are shown and booked in: the browser's own. */
export const browserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';

const fetchDemoInvitation = async (): Promise<DemoInvitationDto> => {
  const { data } = await api.get<ApiResponse<DemoInvitationDto>>(DEMO_INVITATION_PATH);
  if (!data.data) throw new Error('Empty demo invitation response');
  return data.data;
};

/** Whether the free-plan demo invitation shows for the current brand, and where. */
export const useDemoInvitationQuery = (brandId: string | undefined) =>
  useQuery<DemoInvitationDto, Error>({
    queryKey: demoInvitationKeys.brand(brandId ?? ''),
    queryFn: fetchDemoInvitation,
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000,
    // Optional surface: a failed read simply shows nothing.
    retry: false,
  });

const fetchDemoAvailability = async (timeZone: string): Promise<DemoAvailabilityDto> => {
  const { data } = await api.get<ApiResponse<DemoAvailabilityDto>>(
    `${DEMO_INVITATION_PATH}/availability`,
    { params: { timeZone } }
  );
  if (!data.data) throw new Error('Empty demo availability response');
  return data.data;
};

/** The team's next free slots, read only once they are on screen. */
export const useDemoAvailabilityQuery = (enabled: boolean) => {
  const timeZone = browserTimeZone();
  return useQuery<DemoAvailabilityDto, Error>({
    queryKey: demoInvitationKeys.availability(timeZone),
    queryFn: () => fetchDemoAvailability(timeZone),
    enabled,
    staleTime: 60 * 1000,
    retry: 1,
  });
};
